import React, { useMemo, useRef, useCallback } from "react";
import Konva from "konva";
import { Group, Text, Rect } from "react-konva";
import { MondayLogoKonva } from "./logo";
import { IMondayTaskController } from "../../controller";
import { MondayTaskSection } from "shared/datamodel/monday-task";
import { KonvaTooltip } from "frontend/ui-components/design-system/tooltip/konva-tooltip";
import { useMondayTaskInteraction } from "../../hooks/use-monday-task-interaction";
import { ExpandButton } from "./expand-button";
import { FONT_FAMILY, FONT_SIZE } from "../../consts";
import { measureTextWidth } from "../../text-utils";
import useObservableController from "elements/hooks/use-observable-controller";
import tracking from "frontend/tracking";
import { SubitemsIcon } from "./subitems-icon";

const HORIZONTAL_PADDING = 5;
const VERTICAL_PADDING = 6;
const LOGO_SIZE = 17;
const SUBITEMS_ICON_SIZE = 14;

const TITLE_PADDING = 8;
const MAX_TITLE_WIDTH = 195;

const MAX_NAME_WIDTH = 245;
interface MondayTaskHeaderProps extends Konva.ContainerConfig {
  controller: IMondayTaskController;
  allowExpand: boolean;
  contentWidth: number;
}

export const MondayTaskHeader = React.memo(
  ({ controller, allowExpand, contentWidth, ...props }: MondayTaskHeaderProps) => {
    useObservableController(controller, ["item-changed", "size-changed"]);

    const backgroundRef = useRef<Konva.Rect>(null);

    const y = controller.getSectionY(MondayTaskSection.BASE);
    const item = controller.getItem();

    const { isSubitem, boardName } = useMemo(() => {
      if (!item?.parent_item) {
        return { isSubitem: false, boardName: item?.board?.name || "" };
      }

      return { isSubitem: true, boardName: item?.parent_item?.name || "" };
    }, [item]);

    const textWidth = useMemo(() => {
      const { width } = measureTextWidth(boardName, {
        fontSize: FONT_SIZE,
        fontFamily: FONT_FAMILY,
        fontStyle: "normal",
        maxWidth: MAX_TITLE_WIDTH,
      });
      return width;
    }, [boardName]);

    const breadcrumbsTooltip = useMemo(() => {
      const breadcrumbs = controller.breadcrumbs();
      if (!breadcrumbs) return null;

      return breadcrumbs.join(" / ");
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controller.breadcrumbs()]);

    const onClickLink = useCallback(() => {
      if (controller.getItem()?.url) {
        tracking.trackAnalyticsEvent("monday_task_external_link_clicked", {});
        window.open(controller.getItem()?.url, "_blank", "noopener,noreferrer");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controller.getItem()?.url]);

    const handlers = useMondayTaskInteraction({
      elementRef: backgroundRef,
      onClick: onClickLink,
    });

    const backgroundWidth = HORIZONTAL_PADDING + LOGO_SIZE + TITLE_PADDING + textWidth + HORIZONTAL_PADDING * 2;

    const itemName = controller.getItem()?.name || "";

    const textX = isSubitem
      ? LOGO_SIZE + TITLE_PADDING + SUBITEMS_ICON_SIZE + TITLE_PADDING
      : LOGO_SIZE + TITLE_PADDING;

    return (
      <>
        <Group {...props} y={y}>
          <Rect
            ref={backgroundRef}
            fill="transparent"
            x={-HORIZONTAL_PADDING}
            y={-VERTICAL_PADDING}
            width={backgroundWidth}
            height={22}
            {...handlers}
          />

          <MondayLogoKonva width={LOGO_SIZE} height={LOGO_SIZE * 0.6} />

          {isSubitem && <SubitemsIcon x={LOGO_SIZE + TITLE_PADDING} y={-2} />}

          <Text
            x={textX}
            text={boardName}
            fill="#848199"
            fontFamily={FONT_FAMILY}
            fontSize={FONT_SIZE}
            letterSpacing={0.182}
            width={MAX_TITLE_WIDTH}
            ellipsis
            wrap="none"
            listening={false}
          />

          <Text
            y={30}
            text={itemName}
            fill="#1e1e1e"
            fontFamily={FONT_FAMILY}
            fontSize={FONT_SIZE}
            letterSpacing={0.187}
            width={MAX_NAME_WIDTH}
            ellipsis
            wrap="none"
          />

          {allowExpand && <ExpandButton controller={controller} x={contentWidth - 18} y={-6} />}
        </Group>

        <KonvaTooltip
          text={breadcrumbsTooltip || ""}
          nodeRef={backgroundRef}
          side="top"
          maxWidth={330}
          disabled={!breadcrumbsTooltip}
        />
      </>
    );
  }
);

MondayTaskHeader.displayName = "MondayTaskHeader";
